import { JsonValue } from 'type-fest';

export class LocalStorageUtils {
  /**
     * Retrieves the storage data for the given ID.
     *
     * @param storageID - ID of storage to retrieve
     * @returns the storage data if it exists, otherwise undefined.
     */
  public static getLocalStorage(storageID: string): JsonValue | undefined {
    let item: JsonValue = localStorage.getItem(storageID);
    if (typeof item === 'string') {
      item = JSON.parse(item) as JsonValue;
    }

    return item ?? undefined;
  }

  /**
     * Retrieves a specific value from the storage data.
     *
     * @param storageID - ID of storage to retrieve
     * @param item - The item to retrieve.
     * @returns the retrieved value.
     */
  public static getLocalStorageItem<T extends keyof JsonValue>(storageID: string, item: T): JsonValue[T] | undefined {
    const storage = this.getLocalStorage(storageID);

    return (storage) ? storage[item] : undefined;
  }

  /**
     * Sets a specific value in the storage data.
     *
     * @param storageID - ID of storage to set
     * @param item - The item to set.
     * @param value - The value to set.
     */
  public static setLocalStorageItem<T extends keyof JsonValue>(storageID: string, item: T, value: JsonValue[T]): void {
    const storage = this.getLocalStorage(storageID);
    if (storage && storage[item]) {
      storage[item] = value;
      this.setLocalStorage(storageID, storage);
    }
  }

  /**
     * Sets the entire storage data.
     *
     * @param storageID - ID of storage to set
     * @param storage - The storage data to save
     */
  public static setLocalStorage(storageID: string, storage: JsonValue): void {
    localStorage.setItem(storageID, JSON.stringify(storage));
  }
}
